import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Autocomplete,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { displayColumnName } from '../../../api/spreadsheet';
import {
  FieldCategory,
  LookupSearchBy,
  SalesforceFieldForMapping
} from '../../../types/salesforce';
import { HubSpotFieldForMapping } from '../../../types/hubspot';
import { salesforceFieldEligibleForPrimaryKey } from '../../../utils/salesforceMappingUtils';

/** Remove all text in parentheses and trim the result (hides API names like HubSpot field names). */
function stripParenthetical(s: string): string {
  return s
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface ColumnMapping {
  tableColumn: string;
  crmField: string | null;
  allowOverwrite: boolean;
  isPrimaryKey?: boolean;
  /** Salesforce lookup fields only: match sheet values to CRM records by name or id. */
  lookupSearchBy?: LookupSearchBy;
}

interface MappingTableProps {
  mappings: ColumnMapping[];
  crmFields: (SalesforceFieldForMapping | HubSpotFieldForMapping)[];
  isEditMode: boolean;
  activeCrm: 'hubspot' | 'salesforce';
  onMappingChange: (tableColumn: string, crmField: string | null) => void;
  onPrimaryKeyChange: (tableColumn: string, checked: boolean) => void;
  onAllowOverwriteChange: (
    tableColumn: string,
    allowOverwrite: boolean
  ) => void;
  onLookupSearchByChange: (tableColumn: string, value: LookupSearchBy) => void;
}

const MappingTable: React.FC<MappingTableProps> = ({
  mappings,
  crmFields,
  isEditMode,
  activeCrm,
  onMappingChange,
  onPrimaryKeyChange,
  onAllowOverwriteChange,
  onLookupSearchByChange
}) => {
  const getFieldDisplayName = (apiName: string | null): string => {
    if (!apiName) return '—';
    const field = crmFields.find((f) => f.apiName === apiName);
    return field
      ? stripParenthetical(`${field.label} (${field.apiName})`)
      : apiName;
  };

  const sortedCrmFields = useMemo(
    () =>
      [...crmFields].sort((a, b) => {
        const labelA = stripParenthetical(`${a.label} (${a.apiName})`);
        const labelB = stripParenthetical(`${b.label} (${b.apiName})`);
        return labelA.localeCompare(labelB);
      }),
    [crmFields]
  );

  const crmFieldByApiName = useMemo(() => {
    const map = new Map<
      string,
      SalesforceFieldForMapping | HubSpotFieldForMapping
    >();
    for (const f of crmFields) map.set(f.apiName, f);
    return map;
  }, [crmFields]);

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
        Map table columns to CRM fields
      </Typography>
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: '20%',
                  minWidth: 100,
                  fontSize: '0.75rem',
                  py: 0.5
                }}
              >
                Table Column
              </TableCell>
              {isEditMode ? (
                <>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 200,
                      fontSize: '0.75rem',
                      py: 0.5
                    }}
                  >
                    CRM Field
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}
                    align="center"
                    width="88px"
                    title="Use one or more columns together as the composite key for matching CRM records (deduplication)."
                  >
                    Primary key(s)
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}
                    align="center"
                    width="120px"
                  >
                    Overwrite
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}
                    align="center"
                    width="80px"
                  >
                    Options
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      width: '60%',
                      minWidth: 200,
                      fontSize: '0.75rem',
                      py: 0.5
                    }}
                  >
                    CRM Field
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping) => {
              const selectedMeta = mapping.crmField
                ? crmFieldByApiName.get(mapping.crmField)
                : undefined;
              const sfField =
                activeCrm === 'salesforce' && selectedMeta
                  ? (selectedMeta as SalesforceFieldForMapping)
                  : undefined;
              const showLookupMatch =
                activeCrm === 'salesforce' &&
                sfField?.fieldCategory === FieldCategory.LOOKUP;
              const salesforcePkAllowed =
                activeCrm !== 'salesforce' ||
                !mapping.crmField ||
                salesforceFieldEligibleForPrimaryKey(sfField);
              const primaryKeyCheckboxDisabled =
                isEditMode && (!mapping.crmField || !salesforcePkAllowed);
              const primaryKeyTooltip = !mapping.crmField
                ? 'Map a CRM field before marking primary key.'
                : activeCrm === 'salesforce' &&
                    !salesforceFieldEligibleForPrimaryKey(sfField)
                  ? 'This field cannot be used as a primary key.'
                  : '';

              return (
                <TableRow
                  key={mapping.tableColumn}
                  sx={{ '& .MuiTableCell-root': { py: 0.5 } }}
                >
                  <TableCell sx={{ width: '20%', minWidth: 100 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={displayColumnName(mapping.tableColumn)}
                    >
                      {displayColumnName(mapping.tableColumn)}
                    </Typography>
                  </TableCell>
                  {isEditMode ? (
                    <>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Stack spacing={1} sx={{ minWidth: 200, maxWidth: '100%' }}>
                          <Autocomplete
                            size="small"
                            options={sortedCrmFields}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : stripParenthetical(
                                    `${option.label} (${option.apiName})`
                                  )
                            }
                            value={
                              crmFields.find(
                                (f) => f.apiName === mapping.crmField
                              ) ?? null
                            }
                            onChange={(_, newValue) =>
                              onMappingChange(
                                mapping.tableColumn,
                                newValue?.apiName ?? null
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="None"
                                size="small"
                                sx={{
                                  '& .MuiInputBase-root': { minHeight: 40 }
                                }}
                              />
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option.apiName === value?.apiName
                            }
                          />
                          {showLookupMatch && mapping.crmField && (
                            <FormControl fullWidth size="small">
                              <InputLabel
                                id={`lookup-match-${mapping.tableColumn}`}
                              >
                                Lookup match
                              </InputLabel>
                              <Select
                                labelId={`lookup-match-${mapping.tableColumn}`}
                                label="Lookup match"
                                value={
                                  mapping.lookupSearchBy ?? LookupSearchBy.NAME
                                }
                                onChange={(e) =>
                                  onLookupSearchByChange(
                                    mapping.tableColumn,
                                    e.target.value as LookupSearchBy
                                  )
                                }
                              >
                                <MenuItem value={LookupSearchBy.NAME}>
                                  Name (label)
                                </MenuItem>
                                <MenuItem value={LookupSearchBy.ID}>
                                  Salesforce Id
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center" width="88px">
                        <Tooltip
                          title={
                            primaryKeyCheckboxDisabled ? primaryKeyTooltip : ''
                          }
                          arrow
                        >
                          <span>
                            <Checkbox
                              checked={mapping.isPrimaryKey || false}
                              disabled={primaryKeyCheckboxDisabled}
                              onChange={(_, checked) =>
                                onPrimaryKeyChange(mapping.tableColumn, checked)
                              }
                              size="small"
                              sx={{
                                color: (theme) =>
                                  theme.customColors?.themePrimary,
                                '&.Mui-checked': {
                                  color: (theme) =>
                                    theme.customColors?.themePrimary
                                }
                              }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" width="120px">
                        <Tooltip
                          title={
                            mapping.allowOverwrite
                              ? 'Field will be overwritten during updates'
                              : 'Field will be preserved during updates'
                          }
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={mapping.allowOverwrite}
                                onChange={(e) =>
                                  onAllowOverwriteChange(
                                    mapping.tableColumn,
                                    e.target.checked
                                  )
                                }
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: (theme) =>
                                      theme.customColors?.themePrimary ||
                                      'primary.main'
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                    {
                                      backgroundColor: (theme) =>
                                        theme.customColors?.themePrimary ||
                                        'primary.main'
                                    }
                                }}
                              />
                            }
                            label=""
                          />
                        </Tooltip>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center" width="80px">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1}
                        >
                          {mapping.isPrimaryKey && (
                            <Tooltip title="Primary Key" arrow>
                              <VpnKeyIcon
                                fontSize="small"
                                sx={{
                                  color: (theme) =>
                                    theme.customColors?.themePrimary
                                }}
                              />
                            </Tooltip>
                          )}
                          {mapping.allowOverwrite && (
                            <Tooltip title="Overwrite on Update" arrow>
                              <StorageIcon
                                fontSize="small"
                                sx={{
                                  color: (theme) =>
                                    theme.customColors?.themePrimary
                                }}
                              />
                            </Tooltip>
                          )}
                          {!mapping.isPrimaryKey && !mapping.allowOverwrite && (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '60%', minWidth: 200 }}>
                        <Stack spacing={0.25}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={getFieldDisplayName(mapping.crmField)}
                          >
                            {getFieldDisplayName(mapping.crmField)}
                          </Typography>
                          {showLookupMatch && mapping.crmField && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              Lookup match:{' '}
                              {mapping.lookupSearchBy === LookupSearchBy.ID
                                ? 'Salesforce Id'
                                : 'Name (label)'}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MappingTable;
